import { RiArrowDownSLine, RiArrowUpSLine } from '@remixicon/react';
import { FC, useState, useRef, useEffect } from 'react';
import styles from './CapsuleDropdown.module.css';
import MenuDropDown from '../MenuDropDown';
import {
  GroupByConfig,
  GroupTooltipConfig,
  Option,
} from '../../../types/SelectInput';
import { translate } from '../../../localization';
import hasValidValues from '../../../utils/hasValidValues';
import { OutlineButton } from '../../atoms/OutlineButton';

export type CapsuleDropdownProps = {
  name: string;
  list: Option[];
  isDisabled?: boolean;
  label?: string;
  searchInputPlaceholder?: string;
  required?: boolean;
  multiSelect?: boolean;
  value: string[];
  handleSelection: (value: string | string[]) => void;
  className?: string;
  isGroupDropDown?: boolean;
  groupingConfig?: GroupByConfig[];
  groupTooltips?: GroupTooltipConfig;
  unCategorizedListTitle?: string;
  enableSelectAll?: boolean;
  isSearchable?: boolean;
  /** Icon to display before the label */
  iconBefore?: React.ReactNode;
  /** Icon to display after the label (will be overridden by dropdown arrow) */
  iconAfter?: React.ReactNode;
};

const CapsuleDropdown: FC<CapsuleDropdownProps> = ({
  name,
  list,
  label = '',
  searchInputPlaceholder = '',
  required = false,
  multiSelect = false,
  handleSelection,
  isDisabled,
  value,
  className,
  groupingConfig,
  groupTooltips,
  unCategorizedListTitle = translate('others'),
  isGroupDropDown = false,
  enableSelectAll = false,
  isSearchable = false,
  iconBefore,
  iconAfter,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownAlignment, setDropdownAlignment] = useState<'left' | 'right'>(
    'right'
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = () => {
    if (!isDisabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionSelection = (selectedValue: string | string[]) => {
    if (multiSelect) {
      handleSelection(selectedValue as string[]);
    } else {
      handleSelection(selectedValue as string);
      setIsOpen(false);
    }
  };

  const getSelectedOptionLabel = () => {
    const getLabel = (option: string) =>
      list?.find(entry => entry.name === option)?.label || label;

    if (multiSelect) {
      // If no items selected, return the label
      if (!value?.length) {
        return label;
      }

      // If only one item selected, show "Label (1)"
      if (value?.length === 1) {
        return `${label} (1)`;
      }

      // If all items selected, show "Label (All)"
      if (value?.length === list?.length) {
        return `${label} (All)`;
      }

      // For multiple selections (but not all), show "Label (count)"
      return `${label} (${value.length})`;
    }

    // For single select, return the selected option label
    return getLabel(value[0]);
  };

  // Calculate dropdown alignment based on available space
  useEffect(() => {
    if (isOpen && buttonRef.current && dropdownRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 264; // max-width from CSS
      const viewportWidth = window.innerWidth;
      const spaceOnRight = viewportWidth - buttonRect.right;
      const spaceOnLeft = buttonRect.left;

      // Prioritize right alignment (dropdown opens to the left of button) if there's space
      if (spaceOnLeft >= dropdownWidth) {
        setDropdownAlignment('right');
      }
      // Use left alignment (dropdown opens to the right of button) only if there's space
      else if (spaceOnRight >= dropdownWidth) {
        setDropdownAlignment('left');
      }
      // If neither side has enough space, choose the side with more available space
      else {
        setDropdownAlignment(spaceOnLeft > spaceOnRight ? 'right' : 'left');
      }
    }
  }, [isOpen]);

  const buttonLabel =
    value?.length > 0 && hasValidValues(value)
      ? getSelectedOptionLabel()
      : label;

  // Determine the icon after based on dropdown state
  const effectiveIconAfter = isOpen ? (
    <RiArrowUpSLine size={16} />
  ) : (
    iconAfter || <RiArrowDownSLine size={16} />
  );

  return (
    <div className={className} ref={dropdownRef}>
      <OutlineButton
        ref={buttonRef}
        label={buttonLabel}
        iconBefore={iconBefore}
        iconAfter={effectiveIconAfter}
        isDisabled={isDisabled}
        isActive={isOpen}
        onClick={handleClick}
        className={`${styles.capsule_button} ${
          isOpen ? styles['capsule_button--open'] : ''
        }`}
        aria-expanded={isOpen}
        aria-controls={`menu-dd-${name}`}
        aria-disabled={isDisabled}
        role='combobox'
      />

      {required && !value?.length && <span className={styles.required}>*</span>}

      <div
        className={`${styles.options_wrapper} ${
          dropdownAlignment === 'right' ? styles['options_wrapper--right'] : ''
        }`}
        id={`menu-dd-${name}`}
      >
        <div className={styles['options_wrapper--inner']}>
          <MenuDropDown
            selectedValue={multiSelect ? value : value?.[0] || ''}
            list={list}
            valueProperty='name'
            sortBy='label'
            isSorted
            isOpen={isOpen}
            multiSelect={multiSelect}
            onChange={handleOptionSelection}
            isShowSelectElement={false}
            onSelectTabClick={() => {
              if (!isDisabled) {
                setIsOpen(false);
              }
            }}
            wrapperClasses={isOpen ? styles['menu_dropdown--wrapper'] : ''}
            className={styles['menu_dropdown_list--wrapper']}
            searchInputPlaceholder={searchInputPlaceholder}
            groupingConfig={groupingConfig}
            groupTooltips={groupTooltips}
            unCategorizedListTitle={unCategorizedListTitle}
            categorizedDropdown={isGroupDropDown}
            enableSelectAll={enableSelectAll}
            isSearchable={isSearchable}
          />
        </div>
      </div>
    </div>
  );
};

CapsuleDropdown.displayName = 'CapsuleDropdown';

export default CapsuleDropdown;
